/** Firm-facing team and invitation management. */

import { useForm } from "@tanstack/react-form";
import { useQuery as useReactQuery } from "@tanstack/react-query";
import { useAction, useMutation, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import {
	CheckIcon,
	ChevronDownIcon,
	CopyIcon,
	MoreHorizontal,
} from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";

function errorMessage(err: unknown): string {
	if (err instanceof ConvexError) {
		const data = err.data as { message?: string } | undefined;
		if (data?.message) return data.message;
	}
	return "Something went wrong. Please try again.";
}

function initials(name: string): string {
	return name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase() ?? "")
		.join("");
}

function formatDate(ms: number): string {
	return new Date(ms).toLocaleDateString(undefined, {
		year: "numeric",
		month: "long",
		day: "numeric",
	});
}

type Role = "member" | "admin";
type SeatType = "standard" | "premium";
type SeatPlans = Record<
	SeatType,
	{ label: string; monthlyCents: number; annualMonthlyCents: number }
>;
type Interval = "month" | "year";

/** Per-month seat rate for the firm's billing interval. */
function seatRateCents(
	plans: SeatPlans,
	seatType: SeatType,
	interval: Interval,
): number {
	const plan = plans[seatType];
	return interval === "year" ? plan.annualMonthlyCents : plan.monthlyCents;
}

function dollars(cents: number): string {
	return `$${(cents / 100).toLocaleString()}`;
}

function exactDollars(cents: number): string {
	return `$${(cents / 100).toFixed(2)}`;
}

const SEAT_CHOICES: Array<{ role: Role; seatType: SeatType }> = [
	{ role: "member", seatType: "standard" },
	{ role: "member", seatType: "premium" },
	{ role: "admin", seatType: "standard" },
	{ role: "admin", seatType: "premium" },
];

function seatChoiceLabel(
	c: { role: Role; seatType: SeatType },
	plans: SeatPlans,
	interval: Interval,
): string {
	const roleLabel = c.role === "admin" ? "Admin" : "Member";
	const plan = plans[c.seatType];
	return `${roleLabel} ${plan.label} · ${dollars(seatRateCents(plans, c.seatType, interval))}/mo`;
}

/** Option A (member view): same Invite button, but the dialog hands off to a
 * real person — the firm's admins with one-click copyable emails. */
function AskAdminDialog({
	open,
	onOpenChange,
	admins,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	admins: Array<{ name: string; email: string; imageUrl?: string }>;
}) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Ask an admin to invite</DialogTitle>
					<DialogDescription>
						Only firm admins can add members. Your admin
						{admins.length === 1 ? "" : "s"}:
					</DialogDescription>
				</DialogHeader>
				{admins.length === 0 ? (
					<p className="text-sm text-muted-foreground">
						Your firm doesn't have an admin yet — contact Clerical support and
						we'll set one up.
					</p>
				) : (
					<div className="flex flex-col gap-2">
						{admins.map((a) => (
							<div
								key={a.email}
								className="flex items-center gap-3 rounded-lg border px-3 py-2"
							>
								<Avatar className="size-8">
									{a.imageUrl && <AvatarImage src={a.imageUrl} />}
									<AvatarFallback>{initials(a.name)}</AvatarFallback>
								</Avatar>
								<div className="min-w-0 flex-1">
									<div className="truncate font-medium">{a.name}</div>
									<div className="truncate text-sm text-muted-foreground">
										{a.email}
									</div>
								</div>
								<Button
									variant="outline"
									size="sm"
									onClick={() => {
										void navigator.clipboard.writeText(a.email);
										toast.success(`Copied ${a.email}`);
									}}
								>
									<CopyIcon className="size-3.5" />
									Copy email
								</Button>
							</div>
						))}
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}

function InviteDialog({
	open,
	onOpenChange,
	seatPlans,
	interval,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	seatPlans: SeatPlans;
	interval: Interval;
}) {
	const invite = useMutation(api.members.invite);
	const [choice, setChoice] = useState<{ role: Role; seatType: SeatType }>({
		role: "member",
		seatType: "standard",
	});
	const form = useForm({
		defaultValues: { firstName: "", lastName: "", email: "" },
		onSubmit: async ({ value }) => {
			try {
				const result = await invite({ ...value, ...choice });
				const link = `${window.location.origin}/invite/${result.token}`;
				toast.success(`Invited ${value.email} — invite email sent`, {
					action: {
						label: "Copy link",
						onClick: () => void navigator.clipboard.writeText(link),
					},
				});
				form.reset();
				onOpenChange(false);
			} catch (err) {
				toast.error(errorMessage(err));
			}
		},
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Invite a team member</DialogTitle>
					<DialogDescription>
						We'll email them an invite link — they join your firm when they
						accept it.
					</DialogDescription>
				</DialogHeader>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						void form.handleSubmit();
					}}
				>
					<FieldGroup>
						<div className="grid grid-cols-2 gap-3">
							<form.Field name="firstName">
								{(field) => (
									<Field>
										<FieldLabel htmlFor={field.name}>First name</FieldLabel>
										<Input
											id={field.name}
											value={field.state.value}
											onChange={(e) => field.handleChange(e.target.value)}
											required
										/>
									</Field>
								)}
							</form.Field>
							<form.Field name="lastName">
								{(field) => (
									<Field>
										<FieldLabel htmlFor={field.name}>Last name</FieldLabel>
										<Input
											id={field.name}
											value={field.state.value}
											onChange={(e) => field.handleChange(e.target.value)}
											required
										/>
									</Field>
								)}
							</form.Field>
						</div>
						<form.Field name="email">
							{(field) => (
								<Field>
									<FieldLabel htmlFor={field.name}>Email</FieldLabel>
									<Input
										id={field.name}
										type="email"
										placeholder="name@yourfirm.com"
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
										required
									/>
								</Field>
							)}
						</form.Field>
						<Field>
							<FieldLabel>Role &amp; seat</FieldLabel>
							<DropdownMenu>
								<DropdownMenuTrigger
									render={
										<Button
											type="button"
											variant="outline"
											className="w-full justify-between font-normal"
										/>
									}
								>
									{seatChoiceLabel(choice, seatPlans, interval)}
									<ChevronDownIcon className="size-4 text-muted-foreground" />
								</DropdownMenuTrigger>
								<DropdownMenuContent align="start" className="w-64">
									{SEAT_CHOICES.map((c) => (
										<DropdownMenuItem
											key={`${c.role}-${c.seatType}`}
											onClick={() => setChoice(c)}
										>
											{seatChoiceLabel(c, seatPlans, interval)}
											{c.role === choice.role &&
												c.seatType === choice.seatType && (
													<CheckIcon className="ml-auto size-4" />
												)}
										</DropdownMenuItem>
									))}
								</DropdownMenuContent>
							</DropdownMenu>
						</Field>
					</FieldGroup>
					<DialogFooter className="mt-6">
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
						>
							Cancel
						</Button>
						<form.Subscribe selector={(s) => s.isSubmitting}>
							{(isSubmitting) => (
								<Button type="submit" disabled={isSubmitting}>
									{isSubmitting ? "Inviting…" : "Send invite"}
								</Button>
							)}
						</form.Subscribe>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}

/** Cursor-style inline picker: text + chevron for admins, plain text otherwise. */
function InlineSelect<T extends string>({
	value,
	options,
	enabled,
	onSelect,
	onReselect,
}: {
	value: T;
	options: Array<{ value: T; label: string }>;
	enabled: boolean;
	onSelect: (value: T) => void;
	// Fired when the already-selected option is clicked (cancels pending change).
	onReselect?: () => void;
}) {
	const current = options.find((o) => o.value === value)?.label ?? value;
	if (!enabled) return <span>{current}</span>;
	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				render={
					<Button
						variant="ghost"
						size="sm"
						className="-ml-2.5 gap-1 font-normal"
					/>
				}
			>
				{current}
				<ChevronDownIcon className="size-3.5 text-muted-foreground" />
			</DropdownMenuTrigger>
			<DropdownMenuContent align="start">
				{options.map((o) => (
					<DropdownMenuItem
						key={o.value}
						onClick={() => {
							if (o.value !== value) onSelect(o.value);
							else onReselect?.();
						}}
					>
						{o.label}
						{o.value === value && <CheckIcon className="ml-auto size-4" />}
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

type RemovalTarget =
	| { kind: "member"; userId: Id<"users">; name: string }
	| { kind: "invite"; inviteId: Id<"memberInvites">; email: string };

function isUpgrade(
	change: { from: SeatType; to: SeatType },
	plans: SeatPlans,
): boolean {
	return plans[change.to].monthlyCents > plans[change.from].monthlyCents;
}

type ChargePreview =
	| "loading"
	| "error"
	| { dueTodayCents: number; creditAppliedCents: number }
	| null;

/** Confirm-dialog body for a seat change. Downgrades explain the wait-for-
 * renewal policy; upgrades show Stripe's exact charge once the preview lands. */
function seatDialogText(
	change: { name: string; from: SeatType; to: SeatType },
	plans: SeatPlans,
	preview: ChargePreview,
	periodEndMs: number | undefined,
): string {
	if (!isUpgrade(change, plans)) {
		const until = periodEndMs
			? ` until ${formatDate(periodEndMs)}`
			: " until renewal";
		return `${change.name} keeps the ${plans[change.from].label} seat you've already paid for${until}. After that the seat bills at the ${plans[change.to].label} rate. No refunds or credits.`;
	}
	if (preview === "loading") return "Checking the exact charge with Stripe…";
	if (preview === "error" || preview === null) {
		return "Your card will be charged the prorated difference for the rest of this billing period today.";
	}
	if (preview.dueTodayCents === 0) {
		return "Covered by your account credit — nothing charged today.";
	}
	const credit =
		preview.creditAppliedCents > 0
			? ` (${exactDollars(preview.creditAppliedCents)} account credit applied.)`
			: "";
	return `Your card will be charged ${exactDollars(preview.dueTodayCents)} today — the prorated difference for the rest of this billing period.${credit}`;
}

export default function MembersPage() {
	const data = useQuery(api.members.list);
	const removeMember = useMutation(api.members.remove);
	const revokeInvite = useMutation(api.members.revokeInvite);
	const setRole = useMutation(api.members.setRole);
	const setSeatType = useMutation(api.members.setSeatType);
	const [inviteOpen, setInviteOpen] = useState(false);
	const [removal, setRemoval] = useState<RemovalTarget | null>(null);
	const [seatChange, setSeatChange] = useState<{
		userId: Id<"users">;
		name: string;
		from: SeatType;
		to: SeatType;
	} | null>(null);
	const previewSeatChange = useAction(api.billing.teamPage.previewSeatChange);
	const needsChargePreview =
		seatChange !== null &&
		data !== undefined &&
		isUpgrade(seatChange, data.seatPlans);
	const chargePreviewQuery = useReactQuery({
		queryKey: [
			"billing-seat-change-preview",
			seatChange?.userId,
			seatChange?.to,
		],
		queryFn: () => {
			if (seatChange === null) {
				throw new Error("Seat change required");
			}
			return previewSeatChange({
				userId: seatChange.userId,
				seatType: seatChange.to,
			});
		},
		enabled: needsChargePreview,
		retry: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
	});
	const chargePreview: ChargePreview = !needsChargePreview
		? null
		: chargePreviewQuery.isPending || chargePreviewQuery.isFetching
			? "loading"
			: chargePreviewQuery.isError
				? "error"
				: (chargePreviewQuery.data ?? null);
	const [searchParams, setSearchParams] = useSearchParams();

	useState(() => {
		const outcome = searchParams.get("billing");
		if (!outcome) return;
		if (outcome === "success") {
			toast.success("Plan is active — welcome aboard");
		} else if (outcome === "cancelled") {
			toast("Checkout cancelled — no changes made");
		}
		searchParams.delete("billing");
		setSearchParams(searchParams, { replace: true });
	});

	const isAdmin = data?.viewerRole === "admin";

	async function handleSetRole(userId: Id<"users">, role: "admin" | "member") {
		try {
			await setRole({ userId, role });
		} catch (err) {
			toast.error(errorMessage(err));
		}
	}

	function handleSeatSelect(
		m: { userId: Id<"users">; name: string; seatType: SeatType },
		seatType: SeatType,
	) {
		// Money moves (or gets scheduled) on billed tenants — confirm first.
		if (seatType !== m.seatType && data?.billing?.hasTeamSubscription) {
			setSeatChange({
				userId: m.userId,
				name: m.name,
				from: m.seatType,
				to: seatType,
			});
			return;
		}
		void applySeatType(m.userId, m.name, seatType);
	}

	async function applySeatType(
		userId: Id<"users">,
		name: string,
		seatType: SeatType,
	) {
		try {
			const { applied } = await setSeatType({ userId, seatType });
			const label = data?.seatPlans[seatType].label ?? seatType;
			if (applied === "now") {
				toast.success(`${name} is now on a ${label} seat`);
			} else if (applied === "at_renewal") {
				const when = data?.billing?.currentPeriodEnd;
				toast.success(
					`${name} switches to ${label}${when ? ` on ${formatDate(when)}` : " at renewal"}`,
				);
			} else if (applied === "canceled_pending") {
				toast.success(`Scheduled change canceled — ${name} stays on ${label}`);
			}
		} catch (err) {
			toast.error(errorMessage(err));
		}
	}

	async function confirmRemoval() {
		if (!removal) return;
		try {
			if (removal.kind === "member") {
				await removeMember({ userId: removal.userId });
				toast.success(`Removed ${removal.name}`);
			} else {
				await revokeInvite({ inviteId: removal.inviteId });
				toast.success(`Revoked invite for ${removal.email}`);
			}
		} catch (err) {
			toast.error(errorMessage(err));
		} finally {
			setRemoval(null);
		}
	}

	return (
		<div className="flex h-full flex-col">
			<header className="flex shrink-0 items-center border-b py-3 pl-14 pr-6">
				<h1 className="text-lg font-semibold">Members</h1>
			</header>
			<ScrollArea className="min-h-0 flex-1">
				<div className="mx-auto flex max-w-4xl flex-col gap-6 p-6">
					<div className="flex items-center justify-between gap-4">
						<div className="text-sm text-muted-foreground">
							{data?.billing?.hasTeamSubscription ? (
								data.billing.cancelAtPeriodEnd ? (
									<span>
										Team plan ends{" "}
										{data.billing.currentPeriodEnd
											? formatDate(data.billing.currentPeriodEnd)
											: "at period end"}
									</span>
								) : data.billing.currentPeriodEnd ? (
									<span>
										Team plan renews {formatDate(data.billing.currentPeriodEnd)}
									</span>
								) : (
									<span>Team plan</span>
								)
							) : null}
						</div>
						{data && (
							<Button onClick={() => setInviteOpen(true)}>Invite</Button>
						)}
					</div>

					{data === undefined ? (
						<div className="space-y-2">
							<Skeleton className="h-12 w-full" />
							<Skeleton className="h-12 w-full" />
							<Skeleton className="h-12 w-full" />
						</div>
					) : (
						<div className="rounded-lg border">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Member</TableHead>
										<TableHead>Role</TableHead>
										<TableHead>Seat Type</TableHead>
										<TableHead>Status</TableHead>
										{isAdmin && <TableHead className="w-10" />}
									</TableRow>
								</TableHeader>
								<TableBody>
									{data.active.map((m) => (
										<TableRow key={m.userId}>
											<TableCell>
												<div className="flex items-center gap-3">
													<Avatar className="size-8">
														{m.imageUrl && <AvatarImage src={m.imageUrl} />}
														<AvatarFallback>{initials(m.name)}</AvatarFallback>
													</Avatar>
													<div>
														<div className="flex items-center gap-2 font-medium">
															{m.name}
															{m.isViewer && (
																<Badge variant="secondary">You</Badge>
															)}
														</div>
														<div className="text-sm text-muted-foreground">
															{m.email}
														</div>
													</div>
												</div>
											</TableCell>
											<TableCell>
												<InlineSelect
													value={m.role}
													enabled={isAdmin}
													options={[
														{ value: "member", label: "Member" },
														{ value: "admin", label: "Admin" },
													]}
													onSelect={(role) =>
														void handleSetRole(m.userId, role)
													}
												/>
											</TableCell>
											<TableCell>
												<InlineSelect
													value={m.seatType}
													enabled={isAdmin}
													options={[
														{
															value: "standard",
															label: data.seatPlans.standard.label,
														},
														{
															value: "premium",
															label: data.seatPlans.premium.label,
														},
													]}
													onSelect={(seatType) => handleSeatSelect(m, seatType)}
													onReselect={
														m.pendingSeatType
															? () =>
																	void applySeatType(
																		m.userId,
																		m.name,
																		m.seatType,
																	)
															: undefined
													}
												/>
												{m.pendingSeatType && (
													<div className="text-xs text-muted-foreground">
														{data.seatPlans[m.pendingSeatType].label} at renewal
													</div>
												)}
											</TableCell>
											<TableCell>
												<Badge variant="outline">Active</Badge>
											</TableCell>
											{isAdmin && (
												<TableCell>
													{!m.isViewer && (
														<DropdownMenu>
															<DropdownMenuTrigger
																render={<Button variant="ghost" size="icon" />}
															>
																<MoreHorizontal className="size-4" />
															</DropdownMenuTrigger>
															<DropdownMenuContent align="end">
																<DropdownMenuItem
																	variant="destructive"
																	onClick={() =>
																		setRemoval({
																			kind: "member",
																			userId: m.userId,
																			name: m.name,
																		})
																	}
																>
																	Remove
																</DropdownMenuItem>
															</DropdownMenuContent>
														</DropdownMenu>
													)}
												</TableCell>
											)}
										</TableRow>
									))}
									{data.invited.map((inv) => (
										<TableRow key={inv.inviteId}>
											<TableCell>
												<div className="flex items-center gap-3">
													<Avatar className="size-8">
														<AvatarFallback>
															{initials(`${inv.firstName} ${inv.lastName}`)}
														</AvatarFallback>
													</Avatar>
													<div>
														<div className="font-medium">
															{inv.firstName} {inv.lastName}
														</div>
														<div className="text-sm text-muted-foreground">
															{inv.email}
														</div>
													</div>
												</div>
											</TableCell>
											<TableCell className="capitalize">{inv.role}</TableCell>
											<TableCell className="capitalize">
												{inv.seatType}
											</TableCell>
											<TableCell>
												<Badge variant="secondary">Invited</Badge>
											</TableCell>
											{isAdmin && (
												<TableCell>
													<DropdownMenu>
														<DropdownMenuTrigger
															render={<Button variant="ghost" size="icon" />}
														>
															<MoreHorizontal className="size-4" />
														</DropdownMenuTrigger>
														<DropdownMenuContent align="end">
															{inv.token && (
																<DropdownMenuItem
																	onClick={() => {
																		void navigator.clipboard.writeText(
																			`${window.location.origin}/invite/${inv.token}`,
																		);
																		toast.success("Invite link copied");
																	}}
																>
																	Copy invite link
																</DropdownMenuItem>
															)}
															<DropdownMenuItem
																variant="destructive"
																onClick={() =>
																	setRemoval({
																		kind: "invite",
																		inviteId: inv.inviteId,
																		email: inv.email,
																	})
																}
															>
																Revoke invite
															</DropdownMenuItem>
														</DropdownMenuContent>
													</DropdownMenu>
												</TableCell>
											)}
										</TableRow>
									))}
									{data.active.length === 0 && data.invited.length === 0 && (
										<TableRow>
											<TableCell
												colSpan={isAdmin ? 5 : 4}
												className="py-8 text-center text-muted-foreground"
											>
												No members yet.
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</div>
					)}
				</div>
			</ScrollArea>

			{data &&
				(isAdmin ? (
					<InviteDialog
						open={inviteOpen}
						onOpenChange={setInviteOpen}
						seatPlans={data.seatPlans}
						interval={data.billing?.interval ?? "month"}
					/>
				) : (
					<AskAdminDialog
						open={inviteOpen}
						onOpenChange={setInviteOpen}
						admins={data.active.filter((m) => m.role === "admin")}
					/>
				))}

			<AlertDialog
				open={removal !== null}
				onOpenChange={(open) => !open && setRemoval(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{removal?.kind === "invite" ? "Revoke invite?" : "Remove member?"}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{removal?.kind === "invite"
								? `${removal.email} will no longer be able to join your firm with this invite.`
								: removal
									? `${removal.name} will lose access to your firm.`
									: ""}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={() => void confirmRemoval()}
						>
							{removal?.kind === "invite" ? "Revoke" : "Remove"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog
				open={seatChange !== null}
				onOpenChange={(open) => !open && setSeatChange(null)}
			>
				<AlertDialogContent>
					{seatChange && data?.seatPlans && (
						<>
							<AlertDialogHeader>
								<AlertDialogTitle>
									{isUpgrade(seatChange, data.seatPlans)
										? `Upgrade ${seatChange.name} to ${data.seatPlans[seatChange.to].label}?`
										: `Downgrade ${seatChange.name} to ${data.seatPlans[seatChange.to].label}?`}
								</AlertDialogTitle>
								<AlertDialogDescription>
									{seatDialogText(
										seatChange,
										data.seatPlans,
										chargePreview,
										data.billing?.currentPeriodEnd,
									)}
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>Cancel</AlertDialogCancel>
								<AlertDialogAction
									onClick={() => {
										void applySeatType(
											seatChange.userId,
											seatChange.name,
											seatChange.to,
										);
										setSeatChange(null);
									}}
								>
									{isUpgrade(seatChange, data.seatPlans)
										? "Upgrade now"
										: "Schedule downgrade"}
								</AlertDialogAction>
							</AlertDialogFooter>
						</>
					)}
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
